/**
 * Portal Init API Client
 * Uses centralized HTTP client with authentication
 */

import { getHttpClient } from '@spcc/core'
import type { InitData } from '../../domain/types'
import {
  INIT_ENDPOINTS,
  validateMeResponse,
  type MeResponseDto,
} from '../../api/api-types'
import { transformMeResponse } from '../../api/transformers'

export const initApi = {
  /**
   * Get current user/session info from /api/users/me
   *
   * Returns different data based on authentication status:
   * - Guest (no auth): type='guest', featureFlags, ecommerce pricing
   * - Ecommerce user: type='ecommerce', profile, pricingProfile, featureFlags
   * - Customer user: type='customer', profile, customerAccount, settings, featureFlags
   * - Admin user: type='admin', profile, tenants, permissions, featureFlags
   *
   * Note: The HTTP client automatically includes the auth token if available.
   * The backend returns guest data when no valid token is provided.
   */
  async getMe(): Promise<InitData> {
    const http = getHttpClient()

    // The /api/users/me endpoint handles both authenticated and unauthenticated requests
    // Token is automatically included by http client interceptor if available
    const response = await http.get<MeResponseDto>(INIT_ENDPOINTS.ME)

    // Validate response with Zod
    const validated = validateMeResponse(response.data)

    // Transform to domain type
    return transformMeResponse(validated)
  },

  /**
   * Initialize portal - alias for getMe()
   * Called after login to get user context
   */
  async initialize(): Promise<InitData> {
    return this.getMe()
  },

  /**
   * Get guest context
   * Returns feature flags and default ecommerce pricing
   * Note: Returns guest data only if no auth token is stored
   */
  async getGuestContext(): Promise<InitData> {
    return this.getMe()
  },
}
